import { useEffect, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { setLogoutHandler } from './shared/api'

// Refresh the cached token roughly hourly (mirrors asi_ui's TOKEN_REFRESH_INTERVAL).
const TOKEN_REFRESH_INTERVAL = 60 * 60 * 1000

// Allow bypassing auth in local dev via REACT_APP_SKIP_AUTH=true.
const skipAuth =
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_SKIP_AUTH === 'true'

export default function AuthGuard({ children }) {
  const {
    isLoading,
    isAuthenticated,
    loginWithRedirect,
    getAccessTokenSilently,
    logout,
  } = useAuth0()
  const [tokenReady, setTokenReady] = useState(false)

  // Let axiosInstance trigger a logout when the backend returns 401.
  useEffect(() => {
    if (skipAuth) return
    setLogoutHandler(() =>
      logout({
        logoutParams: { returnTo: `${window.location.origin}/login-sso` },
      }),
    )
  }, [logout])

  // Enforce auth on entry: redirect to login if needed, otherwise cache the token.
  useEffect(() => {
    if (skipAuth || isLoading) return

    if (!isAuthenticated) {
      loginWithRedirect({
        appState: {
          target: window.location.pathname + window.location.search,
        },
      })
      return
    }

    let cancelled = false
    const syncToken = async () => {
      try {
        const token = await getAccessTokenSilently()
        if (cancelled) return
        localStorage.setItem('token', token)
        setTokenReady(true)
      } catch {
        if (!cancelled) loginWithRedirect()
      }
    }

    syncToken()
    const id = setInterval(syncToken, TOKEN_REFRESH_INTERVAL)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [isLoading, isAuthenticated, loginWithRedirect, getAccessTokenSilently])

  if (skipAuth) return children

  if (isLoading || !isAuthenticated || !tokenReady) {
    return (
      <div className="auth-loading" role="status" aria-live="polite">
        <span className="spin large" />
      </div>
    )
  }

  return children
}
